import { cn } from "cn";
import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAnchorSnapshot } from "@/hooks/use-anchor-snapshot";
import { useOpenSnapshot } from "@/hooks/use-open-snapshot";
import { partitionSelected } from "@/libs/partition-selected";
import {
  type ComboboxBaseProps,
  ComboboxListSkeleton,
  type ComboboxOption,
  LoadMoreSentinel,
  useComboboxOpen,
} from "./combobox";
import { SelectTrigger } from "./select-trigger";

export type ComboboxMultiProps<T = unknown> = ComboboxBaseProps & {
  value: string[];
  onChange: (value: string[]) => void;
  options: ComboboxOption<T>[];
  /** Custom trigger inner content. Falls back to the selected option labels. */
  renderTrigger?: (selected: ComboboxOption<T>[]) => React.ReactNode;
  /** Selection indicator style. `checkbox` = left checkbox; `check` = right checkmark. */
  indicator?: "check" | "checkbox";
  /** Render selected options in a group above a separator, then the rest. */
  groupSelected?: boolean;
};

export function ComboboxMulti<T = unknown>({
  value,
  onChange,
  options,
  renderTrigger,
  indicator = "check",
  groupSelected = false,
  isLoading,
  search,
  onSearchChange,
  hasMore,
  isLoadingMore,
  onLoadMore,
  placeholder,
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled,
  defaultOpen,
  open,
  onOpenChange,
  modal = true,
  align,
  shouldFilter,
  className,
  ...props
}: ComboboxMultiProps<T>) {
  const [isOpen, setOpen] = useComboboxOpen({
    open,
    defaultOpen,
    onOpenChange,
    disabled,
  });

  // The trigger grows a row of badges/avatars as items are ticked. Freezing the anchor — never the
  // trigger — keeps the open popover where it opened while the trigger resizes under it.
  const anchor = useAnchorSnapshot<HTMLDivElement>(isOpen);

  const selectedSet = new Set(value);
  const isServerSearch = typeof onSearchChange === "function";

  // Dedup lives HERE, once (Trap T3) — domain selects must not re-implement it.
  const toggle = (id: string) => {
    const next = selectedSet.has(id)
      ? value.filter((entry) => entry !== id)
      : Array.from(new Set([...value, id]));
    onChange(next);
  };

  // The trigger summarises the CURRENT selection, so it reads live state.
  const selectedOptions = options.filter((option) =>
    selectedSet.has(option.value),
  );

  // `groupSelected` splits the list by the selection the popover OPENED with, not the live one:
  // regrouping on each tick moves the row across the separator, which remounts it, resets cmdk's
  // highlight to the first row and scrolls the list back to the top. Reopening regroups.
  const openedSelected = useOpenSnapshot(value, { open: isOpen });
  const openedKeys = useMemo(() => new Set(openedSelected), [openedSelected]);
  const {
    selected: openedSelectedOptions,
    unselected: openedUnselectedOptions,
  } = partitionSelected(options, (option) => option.value, openedKeys);

  const renderItem = (option: ComboboxOption<T>) => {
    const isSelected = selectedSet.has(option.value);
    const body = option.detail ?? <span className="grow">{option.label}</span>;
    return (
      <CommandItem
        key={option.value}
        value={option.value}
        keywords={[option.label, ...(option.keywords ?? [])]}
        disabled={option.disabled}
        className={indicator === "checkbox" ? "group" : undefined}
        onSelect={() => toggle(option.value)}
      >
        {indicator === "checkbox" ? (
          <>
            <Checkbox
              checked={isSelected}
              className={cn(
                /* sorted */
                !isSelected &&
                  "opacity-0 group-data-[selected=true]:opacity-100",
              )}
            />
            {body}
          </>
        ) : (
          <>
            {body}
            <BwCheckmarkLine
              size={16}
              className={cn(
                /* sorted */
                "ml-auto shrink-0",
                isSelected ? "opacity-100" : "opacity-0",
              )}
            />
          </>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover modal={modal} open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <SelectTrigger
          {...props}
          ref={anchor.triggerRef}
          isEmpty={value.length === 0}
          className={cn(
            /* sorted */
            "relative",
            className,
          )}
        >
          {renderTrigger ? (
            renderTrigger(selectedOptions)
          ) : (
            <p className="text-sm line-clamp-1 grow">
              {selectedOptions.length > 0
                ? selectedOptions.map((option) => option.label).join(", ")
                : placeholder}
            </p>
          )}
          {anchor.style && (
            <PopoverAnchor asChild>
              <span
                aria-hidden
                className="pointer-events-none"
                style={anchor.style}
              />
            </PopoverAnchor>
          )}
        </SelectTrigger>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 min-w-50"
        align={align}
        data-inline-edit-keep-open
      >
        <Command shouldFilter={shouldFilter ?? !isServerSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            {...(isServerSearch
              ? { value: search, onValueChange: onSearchChange }
              : {})}
          />
          <CommandList>
            {isLoading ? (
              <ComboboxListSkeleton />
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                {groupSelected ? (
                  <>
                    {openedSelectedOptions.length > 0 && (
                      <CommandGroup>
                        {openedSelectedOptions.map(renderItem)}
                      </CommandGroup>
                    )}
                    {openedSelectedOptions.length > 0 &&
                      openedUnselectedOptions.length > 0 && (
                        <CommandSeparator />
                      )}
                    {openedUnselectedOptions.length > 0 && (
                      <CommandGroup>
                        {openedUnselectedOptions.map(renderItem)}
                      </CommandGroup>
                    )}
                  </>
                ) : (
                  <CommandGroup>{options.map(renderItem)}</CommandGroup>
                )}
                {(isLoadingMore || hasMore) && (
                  <LoadMoreSentinel
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={onLoadMore}
                  />
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
